import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeSchoolActorToPush, unsubscribeSchoolActorFromPush } from '../utils/pwa-utils';

const ROLE_PATHS = {
  admin: '/school',
  teacher: '/teacher',
  student: '/student',
  parent: '/parent',
};

// Used as a safe fallback destination when a notification carries no usable url —
// keeps the user inside their own dashboard instead of bouncing to the public site.
const DASHBOARD_PATHS = {
  admin: '/school/admin/dashboard',
  teacher: '/school/teacher/dashboard',
  student: '/school/student/dashboard',
  parent: '/school/parent/dashboard',
};

function getSchoolContext() {
  if (typeof window === 'undefined') return { actor: '', basePath: '', token: '', schoolId: '' };
  const actor = localStorage.getItem('school-actor') || '';
  return {
    actor,
    basePath: ROLE_PATHS[actor] || '',
    dashboardPath: DASHBOARD_PATHS[actor] || '/',
    token: localStorage.getItem('school-token') || '',
    schoolId: localStorage.getItem('school-id') || '',
  };
}

export default function useSchoolNotifications({ limit = 30 } = {}) {
  const contextRef = useRef(getSchoolContext());
  const { actor, basePath, dashboardPath, token, schoolId } = contextRef.current;
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pushStatus, setPushStatus] = useState('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const [preferences, setPreferences] = useState({ push: true, email: true, categories: {} });
  const autoPushAttemptedRef = useRef(false);

  const headers = useCallback(() => {
    const result = { 'x-access-token': token };
    if (schoolId) result['x-school-id'] = schoolId;
    return result;
  }, [schoolId, token]);

  const refresh = useCallback(async () => {
    if (!basePath || !token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${basePath}/notifications?limit=${limit}&skip=0`, { headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to load notifications');
      setItems(Array.isArray(data.items) ? data.items : (data.notifications || []));
      setUnreadCount(Number(data.unreadCount || 0));
    } catch (_) {
      setItems([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [basePath, headers, limit, token]);

  const refreshPushStatus = useCallback(async () => {
    if (!basePath || !token) return;
    try {
      const response = await fetch(`${basePath}/push/status`, { headers: headers() });
      const data = await response.json().catch(() => ({}));
      setPushStatus(data.enabled ? 'enabled' : 'disabled');
    } catch (_) {
      setPushStatus('unknown');
    }
  }, [basePath, headers, token]);

  const refreshPreferences = useCallback(async () => {
    if (!basePath || !token) return;
    try {
      const response = await fetch(`${basePath}/notification-preferences`, { headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.preferences) setPreferences(data.preferences);
    } catch (_) { /* defaults remain active */ }
  }, [basePath, headers, token]);

  const updatePreferences = useCallback(async patch => {
    if (!basePath || !token) return;
    try {
      const response = await fetch(`${basePath}/notification-preferences`, {
        method: 'PATCH',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.preferences) setPreferences(data.preferences);
    } catch (_) { /* best effort */ }
  }, [basePath, headers, token]);

  const markOneRead = useCallback(async (id) => {
    if (!id || !basePath || !token) return;
    try {
      const response = await fetch(`${basePath}/notifications/${encodeURIComponent(id)}/read`, {
        method: 'PATCH',
        headers: headers(),
      });
      if (!response.ok) return;
      setItems(current => current.map(item => String(item._id) === String(id) ? { ...item, status: 'read' } : item));
      setUnreadCount(current => Math.max(0, current - 1));
    } catch (_) { /* best effort */ }
  }, [basePath, headers, token]);

  const markAllRead = useCallback(async () => {
    if (!basePath || !token) return;
    try {
      const response = await fetch(`${basePath}/notifications/mark-all-read`, {
        method: 'POST',
        headers: headers(),
      });
      if (!response.ok) return;
      setItems(current => current.map(item => ({ ...item, status: 'read' })));
      setUnreadCount(0);
    } catch (_) { /* best effort */ }
  }, [basePath, headers, token]);

  const enablePush = useCallback(async ({ requestPermission = true } = {}) => {
    if (!basePath || !token || pushBusy) return false;
    setPushBusy(true);
    try {
      const result = await subscribeSchoolActorToPush({ basePath, token, schoolId, requestPermission });
      if (result.ok) setPushStatus('enabled');
      else if (result.reason === 'permission_denied') setPushStatus('denied');
      return result.ok;
    } finally {
      setPushBusy(false);
    }
  }, [basePath, pushBusy, schoolId, token]);

  const disablePush = useCallback(async () => {
    if (!basePath || !token || pushBusy) return false;
    setPushBusy(true);
    try {
      const result = await unsubscribeSchoolActorFromPush({ basePath, token, schoolId });
      if (result.ok) setPushStatus('disabled');
      return result.ok;
    } finally {
      setPushBusy(false);
    }
  }, [basePath, pushBusy, schoolId, token]);

  useEffect(() => {
    refresh();
    refreshPushStatus();
    refreshPreferences();
  }, [refresh, refreshPreferences, refreshPushStatus]);

  useEffect(() => {
    if (!basePath || !token) return undefined;
    let stream;
    try {
      const query = new URLSearchParams({ token });
      if (schoolId) query.set('schoolId', schoolId);
      stream = new EventSource(`${basePath}/notifications-stream?${query.toString()}`);
      stream.onopen = () => window.dispatchEvent(new CustomEvent('school-exam-changed'));
      stream.onmessage = event => {
        try {
          const data = JSON.parse(event.data || '{}');
          if (data.type !== 'notification.created' && data.type !== 'notification') return;
          const item = data.notification || data.item;
          if (!item?._id) return;
          if (item.entityType === 'SchoolExam') {
            window.dispatchEvent(new CustomEvent('school-exam-changed', { detail: item }));
          }
          setItems(current => [item, ...current.filter(existing => String(existing._id) !== String(item._id))].slice(0, limit));
          setUnreadCount(current => data.unreadCount == null ? current + 1 : Number(data.unreadCount));
          if (item.entityType === 'SchoolExam' && item.meta?.deleted) refresh();
        } catch (_) { /* ignore malformed stream frames */ }
      };
    } catch (_) {
      stream = null;
    }
    return () => { try { stream?.close(); } catch (_) { /* noop */ } };
  }, [basePath, limit, refresh, schoolId, token]);

  useEffect(() => {
    if (autoPushAttemptedRef.current || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    autoPushAttemptedRef.current = true;
    enablePush({ requestPermission: false });
  }, [enablePush]);

  return {
    actor,
    dashboardPath,
    items,
    unreadCount,
    loading,
    pushStatus,
    pushBusy,
    refresh,
    markOneRead,
    markAllRead,
    enablePush,
    disablePush,
    preferences,
    updatePreferences,
  };
}